/**
 * MasterSignupPage — A-3. 일반 회원가입 (사장님/마스터)
 * Step 1: 계정 정보  (가입자명, 아이디+중복확인, 비밀번호+강도, 비밀번호확인, 전화번호+OTP 인증, 이메일, 약관)
 * Step 2: 사업자 정보 (compNo → 인증, 상호명, 대표자명, 주소) → POST /member/join
 *
 * 폼 컨트롤은 전부 ui-core 표준 컴포넌트/클래스다 (D-09 §15.1 버튼 · §15.2 입력 폼).
 * 인라인 style + JS hover/focus 재구현은 2026-09-07 에 걷어냈다 — 색·높이·포커스 링은 토큰이 구동한다.
 */
import { useState, useRef, useId, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@indipia/shared';
import { Button, Input } from '@indipia/ui-core';
import { checkUserExists, checkEmailExists, requestPhoneVerify, verifyPhoneCode } from '../api/member-auth';
import { checkLoginId, verifyBusinessNumber, joinNormal } from '../api/member-join';
import { validateReferralCode } from '../api/referral';
import { loginWithIdPassword } from '../api/auth';
import { applyLoginTokens } from '../lib/post-login';
import { AddressSearchField, AddressSelectResult } from '../components/AddressSearchField';
import { TermsConsentSection, INITIAL_TERMS_CONSENT, type TermsConsentValue } from '../components/TermsConsentSection';
import { PhoneOtpVerify, PhoneOtpSendButton } from '../components/PhoneOtpVerify';
import { PasswordField } from '../components/PasswordField';
import { REFERRAL_CODE_STORAGE_KEY } from './ReferralLandingPage';

// C-8(추천인 초대) — ReferralLandingPage가 저장해둔 코드를 읽기만 한다(제거는 가입 성공 후에만,
// 실패 시 재시도에서도 그대로 쓸 수 있게). 프라이빗 모드 등 저장소 접근 불가는 조용히 무시.
function peekPendingReferralCode(): string | undefined {
  try {
    return localStorage.getItem(REFERRAL_CODE_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function clearPendingReferralCode() {
  try {
    localStorage.removeItem(REFERRAL_CODE_STORAGE_KEY);
  } catch {
    // 저장소 접근 불가 — 무시
  }
}

// ── 비밀번호 강도 ─────────────────────────────────────────────────────────

function pwStrength(pw: string): 0 | 1 | 2 | 3 {
  if (!pw) return 0;
  const hasLetter  = /[a-zA-Z]/.test(pw);
  const hasNumber  = /\d/.test(pw);
  const hasSpecial = /[!@#$%^&*()_+\-=[\]{}|;':",.<>?/]/.test(pw);
  const long       = pw.length >= 8;
  const cats       = [hasLetter, hasNumber, hasSpecial].filter(Boolean).length;
  if (!long || cats < 2) return 1;
  if (cats === 2)        return 2;
  return 3;
}

const PW_LABEL = ['', '약함', '보통', '강함'];
const PW_COLORS = ['var(--shell-border)', 'var(--color-error)', 'var(--color-warning)', 'var(--color-success)'];

/** 비밀번호 강도 바 — PasswordField 의 footer 슬롯에 얹는다 */
function StrengthBar({ password, strength }: { password: string; strength: 0 | 1 | 2 | 3 }) {
  return (
    <>
      <div style={{ display: 'flex', gap: 5, marginTop: 8 }}>
        {[1, 2, 3].map((n) => (
          <div key={n} style={{ flex: 1, height: 3, borderRadius: 2, background: password && strength >= n ? PW_COLORS[strength] : 'var(--shell-border)', transition: 'background .2s' }} />
        ))}
      </div>
      {password && <p className="text-caption" style={{ marginTop: 5, color: PW_COLORS[strength] }}>강도: {PW_LABEL[strength]}</p>}
    </>
  );
}

/** 인디고 브랜드 면(shell.point) 위 흰 계열 — hex·rgba 없이 토큰으로 표현 */
const onPoint = (pct: number) => `color-mix(in srgb, var(--gt-on-primary) ${pct}%, transparent)`;

// ── Stepper (좌측 패널) ───────────────────────────────────────────────────

type StepState = 'done' | 'active' | 'upcoming';

interface StepperProps {
  steps: { label: string; desc: string }[];
  current: number; // 1-based
}

function Stepper({ steps, current }: StepperProps) {
  const getState = (i: number): StepState => {
    if (i + 1 < current) return 'done';
    if (i + 1 === current) return 'active';
    return 'upcoming';
  };

  const iconColor: Record<StepState, React.CSSProperties> = {
    done:     { background: 'var(--alpha-white-200)', border: `1.5px solid ${onPoint(35)}`, color: 'var(--gt-on-primary)' },
    active:   { background: 'var(--shell-surface)', border: '1.5px solid var(--shell-surface)', color: 'var(--shell-point)', boxShadow: `0 0 0 4px ${'var(--alpha-white-200)'}` },
    upcoming: { background: 'transparent', border: `1.5px solid ${onPoint(35)}`, color: 'var(--alpha-white-800)' },
  };
  const numColor:   Record<StepState, string> = { done: 'var(--alpha-white-800)', active: 'var(--gt-on-primary)', upcoming: 'var(--alpha-white-800)' };
  const labelColor: Record<StepState, string> = { done: 'var(--alpha-white-800)', active: 'var(--gt-on-primary)', upcoming: 'var(--alpha-white-800)' };
  const descColor:  Record<StepState, string> = { done: 'var(--alpha-white-800)', active: 'var(--alpha-white-800)', upcoming: 'var(--alpha-white-800)' };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      {steps.map((s, i) => {
        const st = getState(i);
        return (
          <div key={i} style={{ display: 'flex', gap: 16, alignItems: 'flex-start', position: 'relative', paddingBottom: i < steps.length - 1 ? 36 : 0 }}>
            {i < steps.length - 1 && (
              <div style={{ position: 'absolute', left: 15, top: 38, width: 1, height: 40, background: onPoint(35) }} />
            )}
            <div className="text-body-sm font-bold" style={{ width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2, ...iconColor[st] }}>
              {st === 'done' ? '✓' : i + 1}
            </div>
            <div>
              <div className="text-caption font-bold uppercase tracking-widest" style={{ marginBottom: 3, color: numColor[st] }}>
                Step 0{i + 1}
              </div>
              <div className="text-heading-sm font-bold" style={{ color: labelColor[st] }}>{s.label}</div>
              <div className="text-caption" style={{ marginTop: 3, color: descColor[st] }}>{s.desc}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── 메인 컴포넌트 ─────────────────────────────────────────────────────────

const MASTER_STEPS = [
  { label: '계정 정보',   desc: '아이디 · 비밀번호 · 연락처 설정' },
  { label: '사업자 정보', desc: '사업자번호 인증' },
];

export function MasterSignupPage() {
  const navigate    = useNavigate();
  const queryClient = useQueryClient();
  const setAuth     = useAuthStore((s) => s.setAuth);

  const [step, setStep] = useState<1 | 2>(1);

  const loginIdId = useId();
  const mobileId  = useId();
  const compNoId   = useId();

  // ── Step 1(계정 정보) 상태 ─────────────────────────────────────────────
  const [memberName,   setMemberName]   = useState('');
  const [loginId,      setLoginId]      = useState('');
  const [loginIdState, setLoginIdState] = useState<'idle' | 'checking' | 'ok' | 'taken'>('idle');
  const [loginIdFormatErr, setLoginIdFormatErr] = useState<string | null>(null);
  const lastCheckedId  = useRef('');
  const [password,     setPassword]     = useState('');
  const [pwConfirm,    setPwConfirm]    = useState('');

  const [memberNameTouched, setMemberNameTouched] = useState(false);
  const [pwTouched,         setPwTouched]         = useState(false);
  const [pwConfirmTouched,  setPwConfirmTouched]  = useState(false);
  const memberNameErr = memberNameTouched && !memberName.trim() ? '가입자명을 입력해 주세요.' : null;

  const [mobile,        setMobile]        = useState('');
  const [mobileAccountType, setMobileAccountType] = useState<'general' | 'sns' | null>(null);
  const [mobileTouched, setMobileTouched] = useState(false);
  const [otpTouched,    setOtpTouched]    = useState(false);
  const [email,         setEmail]         = useState('');
  const [emailErr,      setEmailErr]      = useState<string | null>(null);
  const [terms,         setTerms]         = useState<TermsConsentValue>(INITIAL_TERMS_CONSENT);
  const [termsTouched,  setTermsTouched]  = useState(false);

  // 전화번호 본인 확인 OTP — 모든 가입자 필수
  const [otpSent,     setOtpSent]     = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  // 인증 완료 토큰 — 가입 요청에 실어 보낸다(같은 번호의 퇴사·거절 직원 계정을 닫을 때 서버가 확인)
  const [verifyToken, setVerifyToken] = useState<string | null>(null);
  const [otpLoading,  setOtpLoading]  = useState(false);
  const [otpError,    setOtpError]    = useState<string | null>(null);

  // ── Step 2(사업자 정보) 상태 ───────────────────────────────────────────
  const [compNo,       setCompNo]       = useState('');
  const [compName,    setCompName]    = useState('');
  const [compRepName,  setCompRepName]  = useState('');
  const [compAddress, setCompAddress] = useState('');
  const [compAddressDtl, setCompAddressDtl] = useState('');
  const [compGpsLat,  setCompGpsLat]  = useState<number | null>(null);
  const [compGpsLng,  setCompGpsLng]  = useState<number | null>(null);
  const [bizVerified, setBizVerified] = useState(false);
  const [bizLoading,  setBizLoading]  = useState(false);
  const [bizMsg,      setBizMsg]      = useState<{ text: string; ok: boolean } | null>(null);

  const [compNameTouched,    setCompNameTouched]    = useState(false);
  const [compRepNameTouched,  setCompRepNameTouched]  = useState(false);
  const [compAddressTouched, setCompAddressTouched] = useState(false);
  const compNameErr    = compNameTouched    && !compName.trim()   ? '상호명을 입력해 주세요.'     : null;
  const compRepNameErr  = compRepNameTouched  && !compRepName.trim() ? '대표자명을 입력해 주세요.'   : null;
  const compAddressErr = compAddressTouched && !compAddress       ? '사업자 주소를 입력해 주세요.' : null;

  // C-8(추천인 초대) — 링크로 들어왔으면 자동 입력, 직접 입력·수정도 가능. 검증은 blur 시점에만
  // 하고 무효해도 제출은 막지 않는다(가입 자체엔 영향 없음, BE가 조용히 무시).
  const [referralCode,    setReferralCode]    = useState(() => peekPendingReferralCode() ?? '');
  const [referralChecking, setReferralChecking] = useState(false);
  const [referralMsg,     setReferralMsg]     = useState<{ text: string; ok: boolean } | null>(null);
  // 마지막으로 요청을 건 코드 — blur를 빠르게 연타하면 먼저 보낸 요청이 나중에 응답할 수 있어
  // (네트워크 지연 역전), 응답이 왔을 때 "그사이 입력이 더 안 바뀌었는지" 확인하는 용도.
  const lastReferralCheckRef = useRef('');
  const checkReferralCode = async (code: string) => {
    const trimmed = code.trim();
    if (!trimmed) {
      // ref도 같이 비워야 한다 — 안 비우면 이전에 날아간(비어있지 않은 값의) 요청이 나중에
      // 응답했을 때 "그사이 안 바뀜"으로 오판해(둘 다 그 옛 값 그대로) 방금 비운 입력창에
      // 낡은 결과를 다시 채워넣는다(2026-09-22 검수에서 발견).
      lastReferralCheckRef.current = '';
      setReferralMsg(null);
      return;
    }
    lastReferralCheckRef.current = trimmed;
    setReferralChecking(true);
    try {
      const result = await validateReferralCode(trimmed);
      if (lastReferralCheckRef.current !== trimmed) return; // 그사이 다른 코드로 재요청됨 — 낡은 응답 무시
      setReferralMsg(result.valid
        ? { text: `${result.referrerName ?? ''} 사장님이 추천했어요`, ok: true }
        : { text: '코드를 찾을 수 없어요. 그래도 가입은 계속 진행돼요.', ok: false });
    } catch {
      if (lastReferralCheckRef.current !== trimmed) return;
      setReferralMsg(null); // 조회 실패는 조용히 통과(가입 자체를 막을 이유는 아님)
    } finally {
      if (lastReferralCheckRef.current === trimmed) setReferralChecking(false);
    }
  };
  // 링크로 들어와 이미 채워진 코드는 blur를 기다리지 않고 마운트 시 1회 자동 확인한다.
  useEffect(() => {
    if (referralCode.trim()) void checkReferralCode(referralCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isLoading,     setIsLoading]     = useState(false);
  const [submitError,   setSubmitError]   = useState<string | null>(null);

  // ── 포맷 헬퍼 ────────────────────────────────────────────────────────────
  const formatPhone = (v: string) => {
    const d = v.replace(/\D/g, '').slice(0, 11);
    if (d.length < 4) return d;
    if (d.length < 8) return `${d.slice(0,3)}-${d.slice(3)}`;
    return `${d.slice(0,3)}-${d.slice(3,7)}-${d.slice(7)}`;
  };

  const formatCompNo = (v: string) => {
    const d = v.replace(/\D/g, '').slice(0, 10);
    if (d.length < 4) return d;
    if (d.length < 6) return `${d.slice(0,3)}-${d.slice(3)}`;
    return `${d.slice(0,3)}-${d.slice(3,5)}-${d.slice(5)}`;
  };

  // ── Step 1 핸들러 ─────────────────────────────────────────────────────────
  const handleCheckLoginId = async () => {
    const id = loginId.trim();
    if (id.length === 0) { setLoginIdFormatErr('아이디를 입력해 주세요.'); return; }
    if (id.length < 6)   { setLoginIdFormatErr('6자 이상 입력해 주세요.'); return; }
    setLoginIdFormatErr(null);
    if (id === lastCheckedId.current && loginIdState !== 'idle') return;
    setLoginIdState('checking');
    try {
      const available = await checkLoginId(id);
      lastCheckedId.current = id;
      setLoginIdState(available ? 'ok' : 'taken');
    } catch { setLoginIdState('idle'); }
  };

  const strength = pwStrength(password);
  const pwValid  = strength >= 2 && /[a-zA-Z]/.test(password) && /\d/.test(password) && /[!@#$%^&*()_+\-=[\]{}|;':",.<>?/]/.test(password) && password.length >= 8;
  const pwErr = pwTouched && !password ? '비밀번호를 입력해 주세요.'
    : pwTouched && !pwValid ? '8자 이상, 영문 + 숫자 + 특수문자를 모두 포함해 주세요.'
    : null;
  const pwConfirmErr = pwConfirmTouched && !pwConfirm ? '비밀번호 확인을 입력해 주세요.'
    : pwConfirm && pwConfirm !== password ? '비밀번호가 일치하지 않아요.'
    : null;
  const pwConfirmOk = !!pwConfirm && pwConfirm === password;

  const handleMobileChange = (v: string) => {
    setMobile(formatPhone(v));
    setMobileAccountType(null);
    setOtpSent(false);
    setOtpVerified(false);
    setVerifyToken(null);
    setOtpError(null);
  };

  // 전화번호 가입 여부 확인 — 결과를 상태에 반영하고 돌려준다(blur·인증번호 발송 전 공용)
  const checkMobileAccount = async (): Promise<'general' | 'sns' | null> => {
    const digits = mobile.replace(/\D/g, '');
    if (digits.length < 10) { setMobileAccountType(null); return null; }
    try {
      const r = await checkUserExists(mobile);
      const type = !r.exists ? null : r.hasPassword ? 'general' : 'sns';
      setMobileAccountType(type);
      return type;
    } catch { setMobileAccountType(null); return null; }
  };

  const handleMobileBlur = async () => {
    setMobileTouched(true);
    await checkMobileAccount();
  };

  const handleOtpRequest = async () => {
    setOtpLoading(true);
    setOtpError(null);
    try {
      // 번호 입력 직후 바로 누르면 blur 중복 확인보다 먼저 실행된다 — 보내기 전에 직접 확인하고, 이미 가입된 번호면 보내지 않는다
      setMobileTouched(true);
      if (await checkMobileAccount() === 'general') return;
      await requestPhoneVerify(mobile);
      setOtpSent(true);
    } catch {
      setOtpError('인증번호 발송에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleOtpVerify = async (code: string) => {
    setOtpLoading(true);
    setOtpError(null);
    try {
      const result = await verifyPhoneCode(mobile, code);
      if (!result.verified) {
        setOtpError('인증번호가 일치하지 않습니다.');
        return;
      }
      setOtpVerified(true);
      setVerifyToken(result.verifyToken);
    } catch {
      setOtpError('인증번호 확인에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleEmailBlur = async () => {
    if (!email.includes('@')) { setEmailErr('이메일 형식이 올바르지 않아요.'); return; }
    try {
      const r = await checkEmailExists(email);
      if (r.exists) setEmailErr('이미 가입된 이메일이에요.');
      else           setEmailErr(null);
    } catch { setEmailErr(null); }
  };

  const mobileDigits    = mobile.replace(/\D/g, '');
  const mobileIsMobileFormat = mobileDigits.startsWith('010') || mobileDigits.startsWith('999'); // 999=테스트 목업 번호
  const mobileReady     = mobileDigits.length >= 10 && mobileIsMobileFormat;
  const mobileErr = mobileTouched && mobileDigits.length >= 10 && !mobileIsMobileFormat ? '010으로 시작하는 휴대폰 번호를 입력해 주세요.'
    : mobileTouched && mobileDigits.length < 10 ? '전화번호를 정확히 입력해 주세요.'
    : null;
  const otpErr    = otpTouched && mobileReady && !otpVerified ? '전화번호 인증을 완료해 주세요.' : null;
  const termsErr  = termsTouched  && !terms.terms ? '필수 약관에 동의해 주세요.' : null;

  /** 전화번호 필드에 걸리는 오류/안내 — `.field` 상태 클래스와 `.help` 를 함께 결정한다 */
  const mobileNotice = mobileErr
    ?? (mobileAccountType === 'general' ? '이미 가입된 전화번호예요. 로그인해 주세요.' : null)
    ?? otpErr;

  const validateStep1 = (): boolean => {
    setMemberNameTouched(true);
    setPwTouched(true);
    setPwConfirmTouched(true);
    setMobileTouched(true);
    setOtpTouched(true);
    setTermsTouched(true);
    let ok = true;
    if (!memberName.trim()) ok = false;
    if (loginIdState !== 'ok') {
      const id = loginId.trim();
      if (id.length === 0)      setLoginIdFormatErr('아이디를 입력해 주세요.');
      else if (id.length < 6)   setLoginIdFormatErr('6자 이상 입력해 주세요.');
      else                      setLoginIdFormatErr('아이디 중복확인을 완료해 주세요.');
      ok = false;
    }
    if (!password || !pwValid) ok = false;
    if (!pwConfirm || pwConfirm !== password) ok = false;
    if (!mobileReady) ok = false;
    else if (!otpVerified) ok = false;
    if (!email.includes('@')) { setEmailErr('이메일 형식이 올바르지 않아요.'); ok = false; }
    else if (emailErr) ok = false;
    if (!terms.terms) ok = false;
    return ok;
  };

  // ── Step 2 핸들러 ─────────────────────────────────────────────────────────
  const handleBizVerify = async () => {
    const raw = compNo.replace(/-/g, '');
    if (raw.length < 10) { setBizMsg({ text: '사업자등록번호 10자리를 입력해 주세요.', ok: false }); return; }
    setBizLoading(true); setBizMsg(null); setBizVerified(false);
    try {
      const r = await verifyBusinessNumber(raw);
      if (r.bizStatus === 'VALID_NEW') {
        setBizVerified(true);
        setBizMsg({ text: '인증되었습니다.', ok: true });
        if (r.companyName && !compName) setCompName(r.companyName);
        if (r.compRepName  && !compRepName) setCompRepName(r.compRepName);
      } else if (r.bizStatus === 'VALID_EXISTING') {
        setBizMsg({ text: '이미 가입된 사업자입니다. 직원으로 가입하거나 로그인해 주세요.', ok: false });
      } else {
        setBizMsg({ text: r.noticeMessage ?? '폐업된 사업자입니다.', ok: false });
      }
    } catch (err) {
      const m = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setBizMsg({ text: m ?? '사업자 조회에 실패했습니다. 다시 시도해 주세요.', ok: false });
    }
    finally { setBizLoading(false); }
  };

  const validateStep2 = (): boolean => {
    setCompNameTouched(true);
    setCompRepNameTouched(true);
    setCompAddressTouched(true);
    let ok = true;
    if (!compName.trim())   ok = false;
    if (!compRepName.trim()) ok = false;
    if (!compAddress)       ok = false;
    if (!bizVerified) {
      const raw = compNo.replace(/-/g, '');
      if (raw.length === 0)      setBizMsg({ text: '사업자등록번호를 입력해 주세요.', ok: false });
      else if (raw.length < 10)  setBizMsg({ text: '사업자등록번호 10자리를 입력해 주세요.', ok: false });
      else                       setBizMsg({ text: '사업자 인증을 완료해 주세요.', ok: false });
      ok = false;
    }
    return ok;
  };

  // ── 최종 제출 ─────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setIsLoading(true); setSubmitError(null);
    try {
      await joinNormal({
        loginId:            loginId.trim(),
        email,
        password,
        memberName:         memberName.trim(),
        mobile,
        verifyToken:        verifyToken ?? undefined,
        master:             true,
        compNo:              compNo.replace(/-/g, ''),
        compType:            'INDIVIDUAL',
        compName:           compName.trim(),
        compRepName:         compRepName.trim(),
        compEmail:          email,
        compAddress:        compAddress || undefined,
        compAddressDtl:     compAddressDtl || undefined,
        compGpsLat:         compGpsLat ?? undefined,
        compGpsLng:         compGpsLng ?? undefined,
        marketingConsent:   terms.marketing,
        marketingChannels:  terms.channels,
        nightMarketingConsent: terms.nightAd,
        // C-8(추천인 초대) — 링크로 자동 채워졌든 직접 입력했든, 입력창에 남아있는 값을 그대로
        // 보낸다(peekPendingReferralCode는 이 입력창의 초기값에만 쓰인다).
        referralCode:       referralCode.trim() || undefined,
      });
      // 1회성 소비 — joinNormal이 성공했을 때만 여기 도달한다(실패 시 catch로 빠져 재시도에도
      // 코드를 그대로 쓸 수 있다).
      clearPendingReferralCode();
    } catch (e) {
      const err = e as { response?: { data?: { code?: string; message?: string } } };
      const code = err?.response?.data?.code;
      if (code === 'DUPLICATE_MOBILE') setSubmitError('이미 일반 계정이 있는 전화번호예요. 로그인해 주세요.');
      else if (code === 'DUPLICATE_LOGIN_ID') setSubmitError('이미 사용 중인 아이디예요. 다른 아이디를 입력해 주세요.');
      else setSubmitError(err?.response?.data?.message ?? '가입 처리 중 오류가 발생했습니다.');
      setIsLoading(false);
      return;
    }

    // ── 가입 성공 → 자동로그인 (실패해도 "가입 성공"은 유지, 로그인 화면으로 폴백) ──
    try {
      const tokens = await loginWithIdPassword(loginId.trim(), password);
      applyLoginTokens(tokens, setAuth, queryClient);
      void navigate('/app', { replace: true });
    } catch {
      void navigate('/login', { replace: true, state: { signupDone: true } });
    } finally {
      setIsLoading(false);
    }
  };

  // ── 렌더 ─────────────────────────────────────────────────────────────────
  return (
    <div className="bg-surface" style={{ display: 'flex', minHeight: '100vh' }}>
      {/* 좌측 패널 */}
      <div className="bg-primary-700" style={{ width: '36%', flexShrink: 0, display: 'flex', flexDirection: 'column', padding: '52px 44px 48px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 52 }}>
          <span className="text-body-sm font-bold uppercase tracking-widest text-white">INDIPIA</span>
          <span className="bg-white/60" style={{ width: 5, height: 5, borderRadius: '50%' }} />
          <span className="text-caption font-semibold uppercase tracking-widest text-[var(--alpha-white-800)]">Pro</span>
        </div>
        <Stepper steps={MASTER_STEPS} current={step} />
        {step === 2 && (
          <div style={{ marginTop: 28, padding: '16px 20px', background: 'var(--alpha-white-200)', border: `1px solid ${onPoint(35)}`, borderRadius: 12 }}>
            <p className="text-caption text-[var(--alpha-white-800)]">
              마지막 단계예요.<br />사업자 정보를 입력하면<br />스튜디오가 바로 시작됩니다.
            </p>
          </div>
        )}
        <div style={{ marginTop: 'auto', paddingTop: 20 }}>
          <span className="text-caption text-[var(--alpha-white-800)]">v1.0 · 2026</span>
        </div>
      </div>

      {/* 우측 폼 */}
      <div className="bg-surface-raised" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '52px 64px', overflowY: 'auto' }}>
        <div style={{ width: '100%', maxWidth: 480 }}>

          {/* ── Step 1: 계정 정보 ───────────────────────────────────────── */}
          {step === 1 && (
            <>
              <div className="text-heading-md font-bold text-text" style={{ marginBottom: 8 }}>계정 정보 입력</div>
              <div className="text-body-sm text-text-secondary" style={{ marginBottom: 28 }}>로그인에 사용할 아이디와 비밀번호를 설정해 주세요.</div>

              <div className="flex flex-col gap-md">
                {/* 가입자명 */}
                <Input
                  label="가입자명" required width="fill" size="lg"
                  type="text" placeholder="이름을 입력하세요" value={memberName}
                  error={memberNameErr ?? undefined}
                  onChange={(e) => setMemberName(e.target.value)}
                  onBlur={() => setMemberNameTouched(true)}
                />

                {/*
                  아이디 — 라벨 아래 "입력 + 우측 버튼"이 한 줄인 조합은 ui-core `Input` 계약으로
                  표현할 수 없다(`Input` 은 `.field > label / input / .help` 를 한 덩어리로 렌더해
                  입력칸만 감싸는 지점이 없다). `.field` 표준 클래스로 직접 조립한다 — 색·높이·
                  포커스 링은 그대로 토큰이 구동한다(D-09 §15.2). `Input` 에 어펜드 슬롯이 생기면
                  회수 대상. — PasswordField.tsx 와 동일 사유.
                */}
                <div className={`field field-fill ${(loginIdFormatErr || loginIdState === 'taken') ? 'error' : loginIdState === 'ok' ? 'success' : ''}`}>
                  <label htmlFor={loginIdId}>아이디<span className="req" aria-hidden="true">*</span></label>
                  <div className="flex gap-sm">
                    <input
                      id={loginIdId}
                      className="input input-lg"
                      type="text" placeholder="6자 이상 영문/숫자" value={loginId}
                      onChange={(e) => { setLoginId(e.target.value); setLoginIdState('idle'); setLoginIdFormatErr(null); }}
                      onBlur={() => {
                        if (loginIdState !== 'idle') return;
                        if (!loginId.trim())                setLoginIdFormatErr('아이디를 입력해 주세요.');
                        else if (loginId.trim().length < 6) setLoginIdFormatErr('6자 이상 입력해 주세요.');
                      }}
                    />
                    <Button
                      variant="secondary" size="lg" className="shrink-0 whitespace-nowrap"
                      loading={loginIdState === 'checking'}
                      onClick={() => void handleCheckLoginId()}
                    >
                      {loginIdState === 'checking' ? '확인 중…' : '중복확인'}
                    </Button>
                  </div>
                  {loginIdFormatErr        && <span className="help">{loginIdFormatErr}</span>}
                  {loginIdState === 'taken' && !loginIdFormatErr && <span className="help">이미 사용 중인 아이디예요.</span>}
                  {loginIdState === 'ok'    && <span className="help">사용 가능한 아이디예요.</span>}
                  {!loginIdFormatErr && loginIdState === 'idle' && (
                    <span className="text-caption text-text-secondary">6자 이상, 영문 소문자와 숫자 조합</span>
                  )}
                </div>

                {/* 비밀번호 */}
                <PasswordField
                  label="비밀번호" required size="lg"
                  placeholder="비밀번호 입력" value={password}
                  error={pwErr ?? undefined}
                  hint="8자 이상 · 영문 + 숫자 + 특수문자 포함"
                  footer={<StrengthBar password={password} strength={strength} />}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={() => setPwTouched(true)}
                />

                {/* 비밀번호 확인 — 일치 여부는 색만이 아니라 문구로도 알린다(D-07 §3.2 #5) */}
                <PasswordField
                  label="비밀번호 확인" required size="lg"
                  className={pwConfirmOk ? 'success' : ''}
                  placeholder="비밀번호를 다시 입력하세요" value={pwConfirm}
                  error={pwConfirmErr ?? undefined}
                  hint={pwConfirmOk ? '비밀번호가 일치해요.' : undefined}
                  onChange={(e) => setPwConfirm(e.target.value)}
                  onBlur={() => setPwConfirmTouched(true)}
                />

                <div style={{ height: 1, background: 'var(--shell-border)', margin: 'var(--spacing-02) 0' }} />

                {/* 전화번호 + 필수 OTP — 위 아이디와 같은 조립 사유 */}
                <div className={`field field-fill ${mobileNotice ? 'error' : ''}`}>
                  <label htmlFor={mobileId}>전화번호<span className="req" aria-hidden="true">*</span></label>
                  <div className="flex gap-sm">
                    <input
                      id={mobileId}
                      className="input input-lg"
                      type="tel" placeholder="010-0000-0000" value={mobile}
                      onChange={(e) => handleMobileChange(e.target.value)}
                      onBlur={() => void handleMobileBlur()}
                    />
                    {mobileReady && mobileAccountType !== 'general' && !otpSent && !otpVerified && (
                      <PhoneOtpSendButton onClick={() => void handleOtpRequest()} loading={otpLoading} />
                    )}
                  </div>
                  {mobileNotice && <span className="help">{mobileNotice}</span>}
                  {!mobileErr && mobileAccountType === 'sns' && (
                    <span className="text-caption text-text-secondary">SNS로 가입된 번호예요. 계속 진행하면 로그인 정보가 연동돼요.</span>
                  )}
                  {!mobileErr && mobileReady && (
                    <PhoneOtpVerify
                      otpSent={otpSent}
                      otpVerified={otpVerified}
                      onVerify={handleOtpVerify}
                      loading={otpLoading}
                      error={otpError}
                      variant="verify"
                    />
                  )}
                </div>

                {/* 이메일 */}
                <Input
                  label="이메일" required width="fill" size="lg"
                  type="email" placeholder="example@email.com" value={email}
                  error={emailErr ?? undefined}
                  onChange={(e) => { setEmail(e.target.value); setEmailErr(null); }}
                  onBlur={() => void handleEmailBlur()}
                />
              </div>

              <div style={{ marginTop: 20 }}>
                <TermsConsentSection value={terms} onChange={setTerms} />
                {termsErr && <p className="text-caption" style={{ marginTop: -14, marginBottom: 16, color: 'var(--color-error)' }}>{termsErr}</p>}
              </div>

              <div className="flex gap-sm" style={{ marginTop: 24 }}>
                <Button variant="etc" size="lg" onClick={() => void navigate('/signup/id')}>취소</Button>
                <Button variant="primary" size="lg" className="flex-1" onClick={() => { if (validateStep1()) setStep(2); }}>
                  다음 →
                </Button>
              </div>
            </>
          )}

          {/* ── Step 2: 사업자 정보 ─────────────────────────────────────── */}
          {step === 2 && (
            <>
              <div className="text-heading-md font-bold text-text" style={{ marginBottom: 8 }}>사업자 정보 확인</div>
              <div className="text-body-sm text-text-secondary" style={{ marginBottom: 28 }}>사업자등록번호로 사업자 정보를 조회합니다.</div>

              <div className="flex flex-col gap-md">
                {/* 사업자등록번호 — 위 아이디와 같은 조립 사유 */}
                <div className={`field field-fill ${bizMsg ? (bizMsg.ok ? 'success' : 'error') : ''}`}>
                  <label htmlFor={compNoId}>사업자등록번호<span className="req" aria-hidden="true">*</span></label>
                  <div className="flex gap-sm">
                    <input
                      id={compNoId}
                      className="input input-lg"
                      type="text" placeholder="000-00-00000" value={compNo} maxLength={12}
                      onChange={(e) => { setCompNo(formatCompNo(e.target.value)); setBizVerified(false); setBizMsg(null); }}
                      onBlur={() => {
                        if (bizVerified) return;
                        const raw = compNo.replace(/-/g, '');
                        if (raw.length === 0)     setBizMsg({ text: '사업자등록번호를 입력해 주세요.', ok: false });
                        else if (raw.length < 10) setBizMsg({ text: '사업자등록번호 10자리를 입력해 주세요.', ok: false });
                      }}
                    />
                    <Button
                      variant="primary" size="lg" className="shrink-0 whitespace-nowrap"
                      loading={bizLoading}
                      onClick={() => void handleBizVerify()}
                    >
                      {bizLoading ? '조회 중…' : '인증'}
                    </Button>
                  </div>
                  {bizMsg && <span className="help">{bizMsg.text}</span>}
                </div>

                <div style={{ height: 1, background: 'var(--shell-border)', margin: 'var(--spacing-02) 0' }} />

                {/* 상호명 */}
                <Input
                  label="상호명" required width="fill" size="lg"
                  type="text" placeholder="예) 인디피아 인테리어" value={compName}
                  error={compNameErr ?? undefined}
                  onChange={(e) => setCompName(e.target.value)}
                  onBlur={() => setCompNameTouched(true)}
                />

                {/* 대표자명 */}
                <Input
                  label="대표자명" required width="fill" size="lg"
                  type="text" placeholder="대표자 성함을 입력하세요" value={compRepName}
                  error={compRepNameErr ?? undefined}
                  onChange={(e) => setCompRepName(e.target.value)}
                  onBlur={() => setCompRepNameTouched(true)}
                />

                {/* 사업자 주소 */}
                <AddressSearchField
                  label="사업자 주소"
                  required
                  value={compAddress}
                  error={compAddressErr}
                  onSelect={(r: AddressSelectResult) => {
                    setCompAddress(r.roadAddress);
                    setCompGpsLat(r.gpsLat);
                    setCompGpsLng(r.gpsLng);
                    setCompAddressTouched(true);
                  }}
                />

                {/* 상세주소 */}
                {compAddress && (
                  <Input
                    label="상세주소" width="fill" size="lg"
                    type="text" placeholder="동/호수 등 상세주소" value={compAddressDtl}
                    onChange={(e) => setCompAddressDtl(e.target.value)}
                  />
                )}

                <div style={{ height: 1, background: 'var(--shell-border)', margin: 'var(--spacing-02) 0' }} />

                {/* 추천인 코드(선택) — C-8. 확인 중엔 아직 결과가 아니므로 success/error 색을 쓰지
                    않고 중립 캡션으로만 표시한다. */}
                <div>
                  <Input
                    label="추천인 코드 (선택)" width="fill" size="lg"
                    type="text" placeholder="추천인의 코드를 입력하세요" value={referralCode}
                    error={!referralChecking && referralMsg && !referralMsg.ok ? referralMsg.text : undefined}
                    success={!referralChecking && referralMsg && referralMsg.ok ? referralMsg.text : undefined}
                    onChange={(e) => { setReferralCode(e.target.value); setReferralMsg(null); }}
                    onBlur={() => void checkReferralCode(referralCode)}
                  />
                  {referralChecking && (
                    <p className="text-caption text-text-secondary" style={{ marginTop: 5 }}>확인 중…</p>
                  )}
                </div>
              </div>

              {submitError && (
                <div style={{ padding: 'var(--spacing-04) var(--spacing-05)', background: 'color-mix(in srgb, var(--color-error) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--color-error) 20%, transparent)', borderRadius: 'var(--radius-md)', marginTop: 16 }}>
                  <p className="text-body-sm" style={{ color: 'var(--color-error)' }}>{submitError}</p>
                </div>
              )}

              <div className="flex gap-sm" style={{ marginTop: 24 }}>
                <Button variant="etc" size="lg" onClick={() => setStep(1)}>← 이전</Button>
                <Button
                  variant="primary" size="lg" className="flex-1"
                  loading={isLoading}
                  onClick={() => { if (validateStep2()) void handleSubmit(); }}
                >
                  {isLoading ? '처리 중…' : '가입 완료'}
                </Button>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
